/** Form designer orchestration: load, edit, validate, save and dirty-state handling. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createForm, deleteForm, getEditableForm, updateForm } from '../../api/forms-api';
import { ConfirmButton } from '../../components/ConfirmButton';
import { ErrorMessage, Loading } from '../../components/Loading';
import { Modal } from '../../components/Modal';
import { useFeedback } from '../../components/Feedback';
import type { DynamicFormDefinition, FieldType, InputType } from '../../models/form';
import { createField, createLayout, createNewForm } from '../../legacy/schema-codec';
import { FormRenderer } from '../renderer/FormRenderer';
import { addFieldToColumn, addLayoutToTab, findField, findLayout, updateField, updateLayout, type DesignerSelection } from './designer-utils';
import { validateDesignerForm, type DesignerIssue } from './designer-validation';
import { DesignerCanvas } from './DesignerCanvas';
import { FormSettings } from './FormSettings';
import { Palette } from './Palette';
import { PropertyEditor } from './PropertyEditor';
import { LayoutPropertyEditor } from './LayoutPropertyEditor';
import { createPersistedFormSettings } from '../../utils/form-settings';
import { useUiLocale } from '../../app/ui-locale';

const HISTORY_LIMIT = 60;

function fingerprint(value: DynamicFormDefinition): string {
  return JSON.stringify(value);
}

function formSettingsFingerprint(value: DynamicFormDefinition): string {
  const { submissionListSettings: _legacySubmissionDefaults, ...editableSettings } = createPersistedFormSettings(value);
  return JSON.stringify(editableSettings);
}

function assertFormSettingsPersisted(expected: DynamicFormDefinition, actual: DynamicFormDefinition): void {
  if (formSettingsFingerprint(expected) !== formSettingsFingerprint(actual)) {
    throw new Error('تنظیمات نمایش سربرگ فرم به‌طور کامل در سرور ذخیره نشد. صفحه را بازخوانی و دوباره تلاش کنید.');
  }
}

export function FormDesignerPage() {
  const params = useParams();
  const formId = params.id ? Number(params.id) : 0;
  const editMode = formId > 0;
  const navigate = useNavigate();
  const { notify } = useFeedback();
  const { locale } = useUiLocale();
  const initialLocaleRef = useRef(locale);
  const initial = useMemo(() => createNewForm(initialLocaleRef.current), []);
  const [form, setForm] = useState<DynamicFormDefinition>(initial);
  const [activeTab, setActiveTab] = useState('Page1');
  const [selection, setSelection] = useState<DesignerSelection>();
  const [sidePanel, setSidePanel] = useState<'form' | 'element'>('form');
  const [viewport, setViewport] = useState<'xs' | 'sm' | 'md' | 'lg'>('lg');
  const [loading, setLoading] = useState(editMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [issuesOpen, setIssuesOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(true);
  const [historyIndex, setHistoryIndex] = useState(0);
  const historyRef = useRef<DynamicFormDefinition[]>([structuredClone(initial)]);
  const historyIndexRef = useRef(0);
  const [savedFingerprint, setSavedFingerprint] = useState(() => fingerprint(initial));

  const resetDocument = useCallback((loaded: DynamicFormDefinition) => {
    const snapshot = structuredClone(loaded);
    historyRef.current = [snapshot];
    historyIndexRef.current = 0;
    setHistoryIndex(0);
    setForm(loaded);
    setSavedFingerprint(fingerprint(loaded));
    setSelection(undefined);
    setActiveTab(loaded.Tabs[0]?.name ?? 'Page1');
  }, []);

  useEffect(() => {
    if (!editMode) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    getEditableForm(formId).then((loaded) => {
      if (!cancelled) resetDocument(loaded);
    }).catch((loadError) => {
      if (!cancelled) setError(loadError);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [editMode, formId, resetDocument]);

  const dirty = useMemo(() => fingerprint(form) !== savedFingerprint, [form, savedFingerprint]);
  const issues = useMemo(() => validateDesignerForm(form), [form]);
  const errorCount = issues.filter((issue) => issue.severity === 'error').length;
  const warningCount = issues.length - errorCount;
  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < historyRef.current.length - 1;

  useEffect(() => {
    const listener = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, [dirty]);

  useEffect(() => {
    if (!form.Tabs.some((tab) => tab.name === activeTab)) setActiveTab(form.Tabs[0]?.name ?? 'Page1');
    if (selection?.kind === 'field' && !findField(form, selection.id)) setSelection(undefined);
    if (selection?.kind === 'layout' && !findLayout(form, selection.id)) setSelection(undefined);
  }, [activeTab, form, selection]);

  const changeForm = useCallback((next: DynamicFormDefinition) => {
    setForm((current) => {
      if (fingerprint(current) === fingerprint(next)) return current;
      let history = historyRef.current.slice(0, historyIndexRef.current + 1);
      history.push(structuredClone(next));
      if (history.length > HISTORY_LIMIT) history = history.slice(history.length - HISTORY_LIMIT);
      historyRef.current = history;
      historyIndexRef.current = history.length - 1;
      setHistoryIndex(historyIndexRef.current);
      return next;
    });
  }, []);

  const goHistory = useCallback((direction: -1 | 1) => {
    const nextIndex = historyIndexRef.current + direction;
    if (nextIndex < 0 || nextIndex >= historyRef.current.length) return;
    historyIndexRef.current = nextIndex;
    setHistoryIndex(nextIndex);
    setForm(structuredClone(historyRef.current[nextIndex]!));
  }, []);

  const selectedField = useMemo(() => selection?.kind === 'field' ? findField(form, selection.id) : undefined, [form, selection]);
  const selectedLayout = useMemo(() => selection?.kind === 'layout' ? findLayout(form, selection.id) : undefined, [form, selection]);
  const active = form.Tabs.find((tab) => tab.name === activeTab) ?? form.Tabs[0];

  const addField = (type: FieldType, inputType?: InputType) => {
    if (!active) return;
    let next = form;
    if (!active.rows.length) {
      next = structuredClone(form);
      next.Tabs.find((tab) => tab.name === active.name)?.rows.push(createLayout(1));
    }
    const current = next.Tabs.find((tab) => tab.name === active.name);
    const lastRow = current?.rows[current.rows.length - 1];
    const firstColumn = lastRow?.columns[0];
    if (!lastRow || !firstColumn) return;
    const field = createField(type, inputType, locale);
    changeForm(addFieldToColumn(next, active.name, lastRow.id, firstColumn.id, field));
    setSelection({ kind: 'field', id: field.id });
    setSidePanel('element');
    setSettingsOpen(true);
  };

  const addLayout = (columns: number) => {
    const layout = createLayout(columns);
    changeForm(addLayoutToTab(form, activeTab, layout));
    setSelection({ kind: 'layout', id: layout.id });
    setSidePanel('element');
    setSettingsOpen(true);
  };

  const save = useCallback(async () => {
    const currentIssues = validateDesignerForm(form);
    const blocking = currentIssues.filter((issue) => issue.severity === 'error');
    if (blocking.length) {
      setIssuesOpen(true);
      notify(`${blocking.length} خطا مانع ذخیره فرم است.`, 'warning');
      return;
    }

    setSaving(true);
    setError(undefined);
    try {
      if (editMode) {
        const expected = { ...form, Id: String(formId) };
        const updated = await updateForm(expected);
        if (!updated) throw new Error('سرور ذخیره فرم را تأیید نکرد.');
        const confirmed = await getEditableForm(formId);
        assertFormSettingsPersisted(expected, confirmed);
        resetDocument(confirmed);
        notify(currentIssues.length ? `فرم ذخیره و کنترل شد؛ ${currentIssues.length} هشدار باقی مانده است.` : 'تغییرات فرم ذخیره و کنترل شد.', currentIssues.length ? 'warning' : 'success');
      } else {
        const id = await createForm(form);
        const expected = { ...form, Id: String(id) };
        const confirmed = await getEditableForm(id);
        assertFormSettingsPersisted(expected, confirmed);
        setSavedFingerprint(fingerprint(confirmed));
        notify('فرم ایجاد و ذخیره آن کنترل شد.', 'success');
        navigate(`/dynamicforms/forms/edit/${id}`, { replace: true });
      }
    } catch (saveError) {
      setError(saveError);
      notify(saveError instanceof Error ? saveError.message : 'خطا در ذخیره فرم', 'error');
    } finally {
      setSaving(false);
    }
  }, [editMode, form, formId, navigate, notify, resetDocument]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const command = event.ctrlKey || event.metaKey;
      if (!command) return;
      const key = event.key.toLowerCase();
      if (key === 's') {
        event.preventDefault();
        void save();
      } else if (key === 'z' && event.shiftKey) {
        event.preventDefault();
        goHistory(1);
      } else if (key === 'z') {
        event.preventDefault();
        goHistory(-1);
      } else if (key === 'y') {
        event.preventDefault();
        goHistory(1);
      }
    };
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, [goHistory, save]);

  const openIssue = (issue: DesignerIssue) => {
    if (issue.tabName && form.Tabs.some((tab) => tab.name === issue.tabName)) setActiveTab(issue.tabName);
    if (issue.fieldId) {
      setSelection({ kind: 'field', id: issue.fieldId });
      setSidePanel('element');
      setSettingsOpen(true);
    } else if (issue.layoutId) {
      setSelection({ kind: 'layout', id: issue.layoutId });
      setSidePanel('element');
      setSettingsOpen(true);
    } else {
      setSidePanel('form');
      setSettingsOpen(true);
    }
    setIssuesOpen(false);
  };

  const leaveDesigner = () => {
    if (dirty && !window.confirm('تغییرات ذخیره‌نشده از بین برود؟')) return;
    navigate('/dynamicforms/dashboard/10/1');
  };

  if (loading) return <Loading text="در حال دریافت فرم…" />;

  return <div className={`df-designer-shell ${!paletteOpen ? 'is-palette-closed' : ''} ${!settingsOpen ? 'is-settings-closed' : ''}`}>
    {error ? <div className="df-designer-error"><ErrorMessage error={error} /></div> : null}
    <div className="df-designer-toolbar" role="toolbar" aria-label="ابزارهای طراحی فرم">
      <button type="button" className="btn btn-default" onClick={leaveDesigner} title="بازگشت به فهرست فرم‌ها">← فهرست</button>
      <div className="btn-group df-history-actions" role="group" aria-label="تاریخچه تغییرات">
        <button type="button" className="btn btn-default" disabled={!canUndo} onClick={() => goHistory(-1)} title="بازگردانی (Ctrl+Z)">↶</button>
        <button type="button" className="btn btn-default" disabled={!canRedo} onClick={() => goHistory(1)} title="انجام مجدد (Ctrl+Y)">↷</button>
      </div>
      <button type="button" className="btn btn-default" onClick={() => setPreviewOpen(true)} title="پیش‌نمایش فرم">◉ پیش‌نمایش</button>
      <button type="button" className={`btn ${errorCount ? 'btn-danger' : warningCount ? 'btn-warning' : 'btn-default'}`} onClick={() => setIssuesOpen(true)} title="بررسی ساختار فرم">بررسی {issues.length ? `(${issues.length})` : '✓'}</button>
      <button type="button" className="btn btn-success" disabled={saving} onClick={() => void save()} title="ذخیره (Ctrl+S)">{saving ? 'در حال ذخیره…' : 'ذخیره تغییرات'}</button>
      {editMode ? <ConfirmButton type="button" className="btn btn-danger" message="فرم و اطلاعات طراحی آن حذف شود؟" onClick={async () => { try { await deleteForm(formId); notify('فرم حذف شد.', 'success'); navigate('/dynamicforms/dashboard/10/1'); } catch (deleteError) { notify(deleteError instanceof Error ? deleteError.message : 'حذف فرم ناموفق بود.', 'error'); } }}>حذف</ConfirmButton> : null}

      <span className="df-form-state" title={dirty ? 'تغییرات ذخیره‌نشده وجود دارد' : 'همه تغییرات ذخیره شده است'}><i className={dirty ? 'is-dirty' : 'is-saved'} /> {editMode ? `فرم ${formId}` : 'فرم جدید'} · {dirty ? 'ذخیره‌نشده' : 'ذخیره‌شده'}</span>

      <div className="df-viewport-buttons" aria-label="عرض پیش‌نمایش">{(['xs', 'sm', 'md', 'lg'] as const).map((size) => <button key={size} type="button" aria-pressed={viewport === size} className={viewport === size ? 'active' : ''} onClick={() => setViewport(size)} title={`نمایش ${size.toUpperCase()}`}>{size.toUpperCase()}</button>)}</div>
      <div className="df-designer-panel-toggles">
        <button type="button" className={paletteOpen ? 'active' : ''} aria-pressed={paletteOpen} onClick={() => setPaletteOpen((value) => !value)}>ابزارها</button>
        <button type="button" className={settingsOpen ? 'active' : ''} aria-pressed={settingsOpen} onClick={() => setSettingsOpen((value) => !value)}>تنظیمات</button>
      </div>
      <div className="df-side-toggle"><button type="button" className={sidePanel === 'form' ? 'active' : ''} onClick={() => { setSidePanel('form'); setSettingsOpen(true); }}>فرم</button><button type="button" className={sidePanel === 'element' ? 'active' : ''} onClick={() => { setSidePanel('element'); setSettingsOpen(true); }}>عنصر</button></div>
    </div>

    <div className="df-designer-grid">
      {paletteOpen ? <Palette form={form} activeTab={activeTab} selection={selection} onAddField={addField} onAddLayout={addLayout} onChangeTab={setActiveTab} onSelect={(nextSelection) => { setSelection(nextSelection); setSidePanel('element'); setSettingsOpen(true); }} /> : null}
      <DesignerCanvas
        form={form}
        activeTab={activeTab}
        selection={selection}
        viewport={viewport}
        onChange={changeForm}
        onSelect={(nextSelection) => {
          setSelection(nextSelection);
          if (nextSelection) {
            setSidePanel('element');
            setSettingsOpen(true);
          }
        }}
        onChangeTab={setActiveTab}
      />
      {settingsOpen ? sidePanel === 'form' ? (
        <FormSettings form={form} activeTab={activeTab} onChange={changeForm} onActiveTabChange={setActiveTab} />
      ) : selection?.kind === 'layout' ? (
        <LayoutPropertyEditor layout={selectedLayout} onUpdate={(layout) => changeForm(updateLayout(form, selection.id, () => layout))} />
      ) : (
        <PropertyEditor form={form} field={selectedField} onUpdate={(field) => selection?.kind === 'field' && changeForm(updateField(form, selection.id, () => field))} />
      ) : null}
    </div>

    <Modal open={previewOpen} title={`پیش‌نمایش: ${form.Title || 'فرم بدون عنوان'}`} wide onClose={() => setPreviewOpen(false)} footer={<button type="button" className="btn btn-default" onClick={() => setPreviewOpen(false)}>بستن پیش‌نمایش</button>}>
      <div className="df-designer-preview"><FormRenderer form={form} readOnly /></div>
    </Modal>

    <Modal open={issuesOpen} title="بررسی ساختار فرم" onClose={() => setIssuesOpen(false)} footer={<><span className="df-issue-summary">{errorCount} خطا · {warningCount} هشدار</span><button type="button" className="btn btn-default" onClick={() => setIssuesOpen(false)}>بستن</button></>}>
      {!issues.length ? <div className="alert alert-success">ساختار فرم بررسی شد و مشکل شناخته‌شده‌ای پیدا نشد.</div> : <div className="df-designer-issues">
        <p className="help-block">برای رفتن به محل هر مورد روی آن کلیک کنید.</p>
        {issues.map((issue, index) => <button type="button" key={`${issue.message}-${index}`} className={`df-designer-issue is-${issue.severity}`} onClick={() => openIssue(issue)}>
          <span>{issue.severity === 'error' ? 'خطا' : 'هشدار'}</span><strong>{issue.message}</strong>{issue.tabName ? <small>مرحله: {form.Tabs.find((tab) => tab.name === issue.tabName)?.title ?? issue.tabName}</small> : null}
        </button>)}
      </div>}
    </Modal>
  </div>;
}
